import clsx from 'clsx'
import {
  type ChangeEvent,
  type ClipboardEvent,
  type Dispatch,
  type SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react'
import toast from 'react-hot-toast'

import {
  FvButton,
  FvLinkButton,
  Icon,
  TextAreaField,
  TextField,
  ValidatedForm,
} from '@fv/client-components'
import { useCurrencyOptions } from '@fv/client-core'
import type {
  FullShipment,
  ShipmentLocation,
  SpotQuoteGroup,
} from '@fv/client-types'
import { fvDate, fvDateUtc } from '@fv/dates'
import {
  ALL_CONTACTS_GROUP_ID,
  type Currency,
  type LoadBidExpiration,
  type Workflow,
} from '@fv/models'
import { dates } from '@fv/models/core'
import type { EquipmentType } from '@fv/models/equipment'

import InfoBox from '@/components/InfoBox'
import CheckboxField from '@/components/inputs/CheckboxField'
import { InputAdornment, InputGroup } from '@/components/inputs/InputGroup'
import { SelectField } from '@/components/inputs/SelectField'
import { ShipperMultiSelect } from '@/components/inputs/ShipperMultiSelect'
import UploadDocumentField from '@/components/inputs/UploadDocumentField'
import { List, ListItem } from '@/components/List'
import { PageHeader } from '@/components/PageHeader'
import { Pinline } from '@/components/Pinline'
import { RouteStopDate } from '@/components/QuoteRequest/RouteStopDate'
import { ShipperSlider } from '@/components/ShipperSlider'
import { ShipperTooltip } from '@/components/ShipperTooltip'
import TruckSelector from '@/components/shared/TruckSelector'
import { useShowBidExpiration } from '@/features/settings/account-settings/hooks'
import { useQuoteSettings } from '@/hooks/settings'
import { useSendSpotQuoteRequests } from '@/hooks/spotQuotes'
import { useRecentGroups } from '@/hooks/spotQuotes/useRecentGroups'
import arrayOfLength from '@/utils/arrayOfLength'
import { getDestination } from '@/utils/shipmentFuncs'

import { useHasPermission } from '../auth/hooks'
import { ExpirationDatePickerPanel } from '../bid-expiration/ExpirationDatePanel'
import DocumentList from '../documents/DocumentList'
import { SliderContentBody } from '../shipment-details-panel/SliderContentLayout'
import { useSpotQuoteGroupsWithUngrouped } from '../spot-quote-contacts/queries'
import {
  SpotQuoteEmailConfirmation,
  SpotQuoteEmailPreview,
} from './SpotQuoteEmailPreview'
import { SpotQuoteGroupEditor } from './SpotQuoteGroupEditor'
import {
  useSpotQuoteContactListStore,
  useSpotQuoteListActions,
  useSpotQuoteListSelected,
} from './store'
import type { SpotQuoteFormData } from './types'
import { useUpdateDeliveryDate } from './useUpdateDeliveryDate'

type EquipmentFieldProps = {
  equipmentTypes: EquipmentType[]
  readOnly: boolean
  setEquipmentTypes: Dispatch<SetStateAction<EquipmentType[]>>
  workflow: Workflow
}

const EquipmentField = ({
  equipmentTypes,
  readOnly,
  setEquipmentTypes,
  workflow,
}: EquipmentFieldProps) => {
  const settingsQuery = useQuoteSettings(workflow)
  const optionTypes = settingsQuery.data?.equipmentOptions ?? []
  const options = optionTypes.map(o => ({
    label: o.name,
    value: o.key,
  }))

  return (
    <ShipperMultiSelect
      classNames={{
        container: 'mb-4',
      }}
      name="alternate-types"
      onChange={nextValues => {
        setEquipmentTypes(nextValues.map(v => v.value))
      }}
      options={options}
      getOptionLabel={o => o.label}
      getOptionValue={o => o.value}
      placeholder="Optional equipment types"
      readOnly={readOnly}
      value={options.filter(o => equipmentTypes.includes(o.value))}
    />
  )
}

type SpotQuoteFormProps = {
  activeLoadIndex: number
  close: () => void
  loads: FullShipment[]
  onSuccess?: () => void
  selectTruck?: (i: number) => void
  formData: SpotQuoteFormData[]
  setFormData: Dispatch<SetStateAction<SpotQuoteFormData[]>>
  groups: Group[]
  setGroups: Dispatch<SetStateAction<Group[]>>
}

const SpotQuoteForm = ({
  activeLoadIndex,
  close,
  loads,
  onSuccess,
  selectTruck,
  setFormData,
  formData,
  groups,
  setGroups,
}: SpotQuoteFormProps) => {
  const { locations, documents, loadId, equipment, workflow } = loads[0]
  const [equipmentTypes, setEquipmentTypes] = useState([
    equipment.type,
    ...(equipment.alternateTypes ?? []),
  ])
  const showBidExpiration = useShowBidExpiration()

  const [uploadDoc, setUploadDoc] = useState(false)
  const [isPickingDate, setPickingDate] = useState(false)
  const [isPreviewing, setPreviewing] = useState(false)
  const [isEditingGroups, setEditingGroups] = useState(false)
  const [shipperMessage, setShipperMessage] = useState('')

  const sendRequests = useSendSpotQuoteRequests(equipment.mode)
  const updateDeliveryDate = useUpdateDeliveryDate()
  const readOnly = sendRequests.isPending || sendRequests.isSuccess
  const textAreaRef = useRef<HTMLTextAreaElement>(null)
  const destination = getDestination({ locations })
  const emailsString = useSpotQuoteContactListStore(s => s.emails)
  const emails = useSpotQuoteListSelected()
  const canManage = useHasPermission('manage-spot-contacts')
  const placeHolderText = `Enter any additional details here for truck ${activeLoadIndex + 1}`
  const { setRawEmail, setEmails } = useSpotQuoteListActions()
  const activeLoad = loads[activeLoadIndex]
  const activeFormData = formData.find(l => l.loadId === activeLoad.loadId)
  const selectedGroups = groups.filter(g => g.checked)

  const handleToggleAllGroups = () => {
    setGroups(prev => {
      const allChecked = !prev.some(g => !g.checked)
      return prev.map(g => ({
        ...g,
        checked: !allChecked,
      }))
    })
  }

  function toggleGroup(e: ChangeEvent<HTMLInputElement>) {
    const { checked, value } = e.target

    setGroups(prev => {
      return prev.map(g => ({
        ...g,
        checked: g.id === value ? checked : g.checked,
      }))
    })
  }

  function onPaste(e: ClipboardEvent<HTMLInputElement>) {
    const message = e.clipboardData.getData('text')

    if (message.length > 500) {
      toast('Max of 500 characters, your message has been shortened.')
    }
  }

  function onValidSubmit() {
    if (readOnly) return

    if (!emails?.length) {
      textAreaRef.current?.setCustomValidity(
        'Please enter a valid email address.',
      )

      textAreaRef.current?.reportValidity()
      return
    }

    if (!equipmentTypes?.length) {
      toast.error('Please select at least one equipment type.')
      return
    }

    const selectedGroupIds = groups.filter(g => g.checked).map(g => g.id)

    sendRequests.mutate(
      {
        emails,
        loadIds: loads.map(x => x.loadId),
        selectedGroupIds,
        shipperMessage,
        bidExpirations: formData.map(x => ({
          loadId: x.loadId,
          bidExpiration: x.bidExpiration,
        })),
        targetPrices: formData.map(x => ({
          loadId: x.loadId,
          targetPrice: x.targetPrice,
          targetPriceCurrency: x.targetPriceCurrency,
        })),
        ...(equipment?.mode === 'ltl' && {
          equipmentTypes,
        }),
      },
      {
        onSuccess: () => {
          setGroups(prev => prev.map(g => ({ ...g, checked: false })))
          setShipperMessage('')
          onSuccess?.()

          if (textAreaRef.current) {
            textAreaRef.current.form?.reset()
            setEmails([])
          }
        },
      },
    )
  }

  return (
    <>
      <div className="bg-fv-beer-xlight border-t-fv-beer-medium basis-1/3 overflow-auto border-t p-8">
        <header className="border-fv-blue-300 mb-5 flex items-center border-b border-dashed pb-2 fa-width-auto">
          <h6 className="mb-0 mr-3 border-r border-gray-200 pr-3">
            Spot quote groups
          </h6>

          <FvLinkButton
            icon={groups.some(g => !g.checked) ? 'check-double' : 'times'}
            onClick={handleToggleAllGroups}
          >
            <span>All</span>
          </FvLinkButton>
        </header>

        <List fcp0>
          {groups.map(g => {
            const name = `group-${g.id}`

            return (
              <ListItem className=" !border-fv-beer-medium" key={name}>
                <CheckboxField
                  checked={g.checked}
                  label={g.label}
                  labelClassName="truncate"
                  name={name}
                  onChange={toggleGroup}
                  tooltipOnOverflow
                  value={g.id}
                />
              </ListItem>
            )
          })}

          {!groups.length && (
            <div className="flex justify-center">
              <Icon icon="sync" size="2x" />
            </div>
          )}
        </List>
      </div>
      <ValidatedForm
        className="bg-fv-beer-xlight border-l-fv-beer-medium border-t-fv-beer-medium flex basis-2/3 flex-col flex-nowrap overflow-auto border-l border-t p-8"
        onValidSubmit={onValidSubmit}
      >
        <div className="relative mb-4 flex-1">
          <TextAreaField
            className="form-control h-full"
            name="email-list"
            placeholder="sandy@example.com, jim@example.com"
            readOnly={readOnly || !canManage}
            ref={textAreaRef}
            value={emailsString}
            onChange={e => setRawEmail(e.target.value)}
            required
          />
          <FvButton
            className="bg-fv-blue-50 absolute right-0 top-0 z-40 h-auto border border-[#b3b3b3] px-2 py-1"
            icon="pen-alt"
            size="xs"
            onClick={() => setEditingGroups(true)}
          >
            Edit
          </FvButton>
        </div>

        {equipment.mode === 'ltl' && (
          <>
            <div className="input-group mb-4">
              <InputAdornment
                icon="calendar-day"
                iconClass="text-fv-gray-dark"
                position="start"
                htmlFor="delivery-date"
              />
              <TextField
                className="form-control"
                name="delivery-date"
                placeholder="Delivery date"
                readOnly
                onFocus={() => setPickingDate(true)}
                value={
                  destination?.stopDate
                    ? fvDate(destination?.stopDate).format('ddd MMM D')
                    : ''
                }
              />
            </div>

            <EquipmentField
              equipmentTypes={equipmentTypes}
              readOnly={readOnly || !!equipment.alternateTypes?.length}
              setEquipmentTypes={setEquipmentTypes}
              workflow={workflow}
            />
          </>
        )}
        <div className="mb-3 grid grid-cols-12 gap-x-2">
          <TargetPriceForm
            activeLoadIndex={activeLoadIndex}
            numberOfTrucks={loads.length}
            value={activeFormData.targetPrice}
            currency={activeFormData.targetPriceCurrency}
            locations={activeLoad.locations}
            onChange={targetPrice =>
              setFormData(prev =>
                prev.map(p =>
                  p.loadId === activeLoad.loadId ? { ...p, targetPrice } : p,
                ),
              )
            }
            onCurrencyChange={targetPriceCurrency =>
              setFormData(prev =>
                prev.map(p =>
                  p.loadId === activeLoad.loadId
                    ? { ...p, targetPriceCurrency }
                    : p,
                ),
              )
            }
            selectTruck={selectTruck}
          />
          {showBidExpiration && (
            <BidExpirationForm
              value={activeFormData.bidExpiration}
              onChange={bidExpiration =>
                setFormData(prev =>
                  prev.map(p =>
                    p.loadId === activeLoad.loadId
                      ? { ...p, bidExpiration }
                      : p,
                  ),
                )
              }
            />
          )}
        </div>

        <TextField
          className="form-control mb-4"
          maxLength={500}
          name="user-message"
          onChange={e => setShipperMessage(e.target.value)}
          onPaste={onPaste}
          placeholder={placeHolderText}
          readOnly={readOnly}
          value={shipperMessage}
        />

        <div className="flex items-center self-end">
          <ShipperTooltip label="Cancel / close panel">
            <FvButton icon="times" onClick={close} theme="plain" />
          </ShipperTooltip>
          <FvButton
            icon="file-plus"
            onClick={() => setUploadDoc(true)}
            theme="plain"
          >
            Attach doc(s)
          </FvButton>

          <FvButton
            icon="eye"
            onClick={() => setPreviewing(true)}
            theme="plain"
          >
            View email
          </FvButton>

          <FvButton theme="primary" icon="paper-plane" fwd type="submit">
            <span>Send</span>
            <span className="1550:hidden"> out for quotes</span>
          </FvButton>
        </div>
      </ValidatedForm>

      <ShipperSlider
        width="32rem"
        onClose={() => setUploadDoc(false)}
        isOpen={uploadDoc}
      >
        <SliderContentBody>
          <PageHeader
            className="mx-0 flex items-center justify-between"
            noBorder
            title="Attach document(s)"
          >
            <FvButton
              icon="times"
              onClick={() => setUploadDoc(false)}
              theme="plain"
            >
              Close
            </FvButton>
          </PageHeader>
          <InfoBox>
            Document(s) will be sent to the carrier prior to them quoting on
            your shipment. Any documents already sent will be shown below.
          </InfoBox>
          <DocumentList
            className="mb-6 overflow-auto"
            canDelete={true}
            {...{ documents, loadId }}
          />
          <UploadDocumentField loadId={loadId} defaultDocType={'other'} />
        </SliderContentBody>
      </ShipperSlider>

      <SpotQuoteEmailPreview
        onClose={() => setPreviewing(false)}
        isOpen={isPreviewing}
        loadId={loads[activeLoadIndex].loadId}
        equipmentTypes={equipment?.mode === 'ltl' ? equipmentTypes : undefined}
        shipperMessage={shipperMessage}
        targetPrice={activeFormData?.targetPrice}
        targetPriceCurrency={activeFormData?.targetPriceCurrency}
        bidExpirationLocal={activeFormData?.bidExpiration?.local}
      />

      <SpotQuoteEmailConfirmation
        onClose={() => sendRequests.reset()}
        emails={sendRequests.variables?.emails}
        isOpen={sendRequests.isSuccess}
        workflow={loads[activeLoadIndex].workflow}
      />

      <SpotQuoteGroupEditor
        onClose={() => setEditingGroups(false)}
        isOpen={isEditingGroups}
        initialGroupId={
          selectedGroups.length === 1 ? selectedGroups[0].id : undefined
        }
      />

      <RouteStopDate
        onClose={() => setPickingDate(false)}
        isOpen={isPickingDate}
        sequence={1}
        onChange={data => {
          updateDeliveryDate.mutate({
            date: data.stopDate,
            loadId,
          })
        }}
        showStopDetailConfig={false}
        stops={locations}
      />
    </>
  )
}

type SpotQuotePanelProps = {
  activeLoadIndex?: number
  className?: string
  defaultOpen?: boolean
  label: string
  loads?: FullShipment[]
  onSuccess?: () => void
  selectTruck?: (i: number) => void
}

export const SpotQuotePanel = ({
  activeLoadIndex = 0,
  className,
  defaultOpen = false,
  label,
  loads,
  onSuccess,
  selectTruck,
}: SpotQuotePanelProps) => {
  const [isOpen, setIsOpen] = useState(defaultOpen)
  const [formData, setFormData] = useState<SpotQuoteFormData[]>(
    loads.map(l => ({
      loadId: l.loadId,
      targetPrice: l.targetPrice,
      targetPriceCurrency: l.targetPriceCurrency,
      bidExpiration: l.bidExpiration
        ? { ...l.bidExpiration, utc: fvDateUtc(l.bidExpiration.utc).toDate() }
        : undefined,
    })),
  )
  const groupsQuery = useSpotQuoteGroupsWithUngrouped()
  const recentGroups = useRecentGroups(loads[0].workflow)

  const [groups, setGroups] = useGroups({
    spotQuoteGroups: groupsQuery.data,
    recentGroupIds: recentGroups.isLoading ? undefined : recentGroups.ids,
  })

  function localOnSuccess() {
    onSuccess?.()
    setIsOpen(false)
  }

  return (
    <div
      className={clsx(
        'flex w-full flex-col flex-nowrap items-stretch bg-fv-gray-50 transition-all duration-300 ease-in-out',
        isOpen
          ? 'max-h-[60vh] [@media(max-height:600px)]:max-h-[50vh] basis-[75%]'
          : 'max-h-[3.5rem] basis-0',
        className,
      )}
      key={loads?.[activeLoadIndex].loadId}
    >
      <button
        className="bg-fv-beer-xlight border-t-fv-beer-medium relative flex justify-center border-t p-4 shadow-[0_-.35rem_.75rem_-.25rem_rgba(0,0,0,0.075)]"
        onClick={e => {
          e.preventDefault()
          setIsOpen(o => !o)
        }}
      >
        <div className="border-fv-blue absolute -top-[1.4rem] flex aspect-square w-8 items-center justify-center rounded-full border-2 bg-white">
          <Icon icon={!isOpen ? 'arrow-up' : 'arrow-down'} transform="" />
        </div>
        {!isOpen && <Icon icon="paper-plane" transform="down-4" />}
        <span className="underline underline-offset-[.35rem] decoration-fv-blue-300">
          {!isOpen ? label : 'Cancel / Close panel'}
        </span>
      </button>

      <div
        className={clsx(
          'flex flex-1 flex-nowrap justify-center overflow-hidden',

          {
            'items-stretch': !!loads?.length,
            'items-center': !loads?.length,
          },
        )}
      >
        {loads?.length ? (
          <SpotQuoteForm
            activeLoadIndex={activeLoadIndex}
            close={() => setIsOpen(false)}
            loads={loads}
            onSuccess={localOnSuccess}
            selectTruck={selectTruck}
            formData={formData}
            setFormData={setFormData}
            setGroups={setGroups}
            groups={groups}
          />
        ) : (
          <Icon icon="sync" size="3x" />
        )}
      </div>
    </div>
  )
}

type Group = {
  checked: boolean
  emails: string[]
  id: string
  label: string
}

function useGroups({
  recentGroupIds,
  spotQuoteGroups,
}: {
  recentGroupIds?: string[]
  spotQuoteGroups?: SpotQuoteGroup[]
}): [Group[], Dispatch<SetStateAction<Group[]>>] {
  const [groups, setGroups] = useState<Group[]>([])
  const { addEmails, removeEmails } = useSpotQuoteListActions()
  useEffect(() => {
    if (!spotQuoteGroups?.length || recentGroupIds === undefined) return
    setGroups(prev =>
      spotQuoteGroups.map(g => {
        const { groupId, groupName, users } = g
        return {
          checked:
            prev?.find(g => g.id === groupId)?.checked ??
            recentGroupIds.includes(groupId),
          emails: users.map(u => u.email),
          id: groupId,
          label: groupName,
        }
      }),
    )
  }, [recentGroupIds, spotQuoteGroups])

  useEffect(() => {
    const allContacts = groups.find(g => g.id === ALL_CONTACTS_GROUP_ID)
    const otherGroups = groups.filter(g => g.id !== ALL_CONTACTS_GROUP_ID)
    if (allContacts?.checked) {
      addEmails(allContacts.emails)
    } else if (allContacts) {
      removeEmails(allContacts.emails)
    }
    removeEmails(otherGroups.filter(g => !g.checked).flatMap(g => g.emails))
    addEmails(otherGroups.filter(g => g.checked).flatMap(g => g.emails))
  }, [groups, addEmails, removeEmails])

  return [groups, setGroups]
}

type TargetPriceFormProps = {
  numberOfTrucks: number
  activeLoadIndex: number
  value?: number
  currency?: Currency
  locations?: ShipmentLocation[]
  onChange: (val: number) => void
  onCurrencyChange: (val: Currency) => void
  selectTruck?: (i: number) => void
}

const TargetPriceForm = ({
  numberOfTrucks,
  activeLoadIndex,
  value,
  currency,
  locations,
  onChange,
  onCurrencyChange,
  selectTruck,
}: TargetPriceFormProps) => {
  const showBidExpiration = useShowBidExpiration()
  const currencyOptions = useCurrencyOptions(locations)
  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(parseFloat(e.target.value))
  }

  return (
    <>
      {numberOfTrucks > 1 && (
        <div className="flex col-span-12 mb-3 gap-x-2 items-center">
          <div className="flex items-center gap-x-1">
            <span className="mr-1">Truck</span>

            {arrayOfLength(numberOfTrucks).map(i => (
              <TruckSelector
                active={i === activeLoadIndex}
                key={i}
                selectTruck={() => selectTruck?.(i)}
                truckNumber={i + 1}
              />
            ))}
          </div>
          <Pinline dashed flex className="!border-fv-blue-300" />
        </div>
      )}

      <InputGroup
        className={clsx(
          'mb-0',
          showBidExpiration ? 'col-span-6' : 'col-span-12',
        )}
        inputType="text"
        inputProps={{
          type: 'number',
          name: 'target-price',
          onChange: handlePriceChange,
          value: value ?? '',
          min: 1,
        }}
        startContent={
          <>
            <InputAdornment
              position="start"
              label="Target"
              className="whitespace-nowrap"
            />
            <InputAdornment position="start" icon="dollar-sign" />
          </>
        }
        endContent={
          <SelectField
            className="-ml-px w-auto"
            name="target-price-currency"
            options={currencyOptions}
            value={currency ?? 'usd'}
            onChange={e => onCurrencyChange(e.target.value as Currency)}
          />
        }
      />
    </>
  )
}

type BidExpirationFormProps = {
  value?: LoadBidExpiration
  onChange: (exp: LoadBidExpiration) => void
}

export const BidExpirationForm = ({
  value,
  onChange,
}: BidExpirationFormProps) => {
  const [showDatePicker, setShowDatePicker] = useState(false)
  const handleSetDate = (expireDateTime: string | Date | undefined) => {
    onChange({
      local: dates.dateToStringWithLocalTimezoneAbbr(expireDateTime),
      utc: !expireDateTime ? null : fvDate(expireDateTime).utc().toDate(),
    })
  }

  return (
    <>
      <div className="input-group col-span-6">
        <InputAdornment
          position="start"
          label="Expires on"
          className="whitespace-nowrap"
        />
        <InputAdornment position="start">
          <FvButton
            icon="calendar-day"
            theme="default"
            onClick={() => setShowDatePicker(true)}
          />
        </InputAdornment>
        <TextField
          className="form-control"
          name="expiration-date"
          placeholder=""
          readOnly
          onFocus={() => setShowDatePicker(true)}
          value={value?.utc ? fvDate(value.utc).format('MMM DD, h:mm A') : ''}
        />
      </div>
      <ExpirationDatePickerPanel
        value={value?.utc?.toString() ?? ''}
        onClose={() => setShowDatePicker(false)}
        open={showDatePicker}
        onSave={dateTime => {
          handleSetDate(dateTime)
          setShowDatePicker(false)
        }}
      />
    </>
  )
}
